// useChannelMessages.js

import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';

// Channels
import {
  CHANNEL_MESSAGE_PAGE_SIZE,
  subscribeToChannelMessages,
} from 'channels/services/ChannelReadService';
import { buildThreadSummariesByRootKey } from 'channels/utils/threadSummaries';
import {
  deleteChannelMessage,
  editChannelMessage,
  postChannelMessage,
} from 'channels/services/ChannelWriteService';

// Utilities
import { generateKey } from 'utilities/Keys';
import { getPersonName, getPersonPhoto } from 'utilities/PersonIdentity';
import { buildUserSummary } from 'utilities/Users';

/**
 * useChannelMessages
 *
 * Subscribes to a channel feed and exposes send/edit/delete actions.
 *
 * @param {object} options - Hook options.
 * @param {string} options.baseKey - Base key.
 * @param {string} options.channelKey - Channel key.
 * @param {object} options.user - Active user.
 *
 * @returns {object} Messages state and action handlers.
 */
const useChannelMessages = ({ baseKey, channelKey, user }) => {
  const [messages, setMessages] = useState([]);
  const [allMessages, setAllMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [windowSize, setWindowSize] = useState(CHANNEL_MESSAGE_PAGE_SIZE);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);

  const normalizeNodeReferences = useCallback((nodeReferences = []) => {
    const seen = new Set();

    return (Array.isArray(nodeReferences) ? nodeReferences : [])
      .map((row) => {
        const nodeKey = String(row?.nodeKey || row?.key || '').trim();
        if (!nodeKey) return null;
        if (seen.has(nodeKey)) return null;
        seen.add(nodeKey);

        return {
          key: generateKey(),
          type: 'node_ref',
          nodeKey,
          title: String(row?.title || '').trim(),
          nodeType: String(row?.nodeType || row?.type || '').trim() || 'NODE',
        };
      })
      .filter(Boolean);
  }, []);

  useLayoutEffect(() => {
    if (!baseKey || !channelKey) return;
    setLoading(true);
  }, [baseKey, channelKey]);

  // A different channel starts from the newest page again.
  useEffect(() => {
    setWindowSize(CHANNEL_MESSAGE_PAGE_SIZE);
    setHasMoreMessages(false);
  }, [baseKey, channelKey]);

  useEffect(() => {
    if (!baseKey || !channelKey) {
      setMessages([]);
      setAllMessages([]);
      setLoading(false);
      setErrorMessage('');
      return () => {};
    }

    setLoading(true);
    setErrorMessage('');

    const unsubscribe = subscribeToChannelMessages(
      baseKey,
      channelKey,
      (rows, meta) => {
        const cleanRows = Array.isArray(rows) ? rows : [];
        const topLevelRows = cleanRows.filter((row) => !row?.parentMessageKey);
        setAllMessages(cleanRows);
        setMessages(topLevelRows);
        setHasMoreMessages(Boolean(meta?.hasMore));
        setLoading(false);
        setIsLoadingOlder(false);
      },
      (error) => {
        setErrorMessage(error?.message || 'Failed to load channel messages.');
        setLoading(false);
        setIsLoadingOlder(false);
      },
      { windowSize }
    );

    return () => unsubscribe && unsubscribe();
  }, [baseKey, channelKey, windowSize]);

  /**
   * Widen the live window by one page. Growing a single listener (rather than
   * appending a separately fetched page) keeps edits and deletes correct and cannot
   * leave a gap when new messages push the window forward.
   */
  const loadOlderMessages = useCallback(() => {
    if (!hasMoreMessages || isLoadingOlder) return;
    setIsLoadingOlder(true);
    setWindowSize((current) => current + CHANNEL_MESSAGE_PAGE_SIZE);
  }, [hasMoreMessages, isLoadingOlder]);

  const sendMessage = useCallback(async ({
    text,
    nodeReferences = [],
    mentionKeys = [],
    messageType,
  } = {}) => {
    if (!baseKey || !channelKey || !user?.key) return false;

    const cleanText = String(text || '').trim();
    const nodeReferenceAttachments = normalizeNodeReferences(nodeReferences);
    const cleanMentionKeys = Array.from(new Set(
      (Array.isArray(mentionKeys) ? mentionKeys : [])
        .map((key) => String(key || '').trim())
        .filter(Boolean)
    ));

    if (!cleanText && nodeReferenceAttachments.length === 0) return false;

    setErrorMessage('');
    setIsSending(true);

    try {
      const userSummary = buildUserSummary(user);

      const created = await postChannelMessage({
        baseKey,
        channelKey,
        userKey: user.key,
        user: userSummary,
        userName: userSummary?.name || getPersonName(user, 'Member'),
        userPhoto: userSummary?.coverUrl || getPersonPhoto(user, ''),
        text: cleanText,
        attachments: nodeReferenceAttachments,
        mentionKeys: cleanMentionKeys,
        messageType: messageType || undefined,
      });

      return created;
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to send message.');
      return false;
    } finally {
      setIsSending(false);
    }
  }, [baseKey, channelKey, user, normalizeNodeReferences]);

  const updateMessage = useCallback(async ({ messageKey, text }) => {
    if (!baseKey || !channelKey || !messageKey) return false;

    try {
      await editChannelMessage({
        baseKey,
        channelKey,
        messageKey,
        text,
      });
      return true;
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to edit message.');
      return false;
    }
  }, [baseKey, channelKey]);

  const removeMessage = useCallback(async ({ messageKey }) => {
    if (!baseKey || !channelKey || !messageKey || !user?.key) return false;

    try {
      await deleteChannelMessage({
        baseKey,
        channelKey,
        messageKey,
        userKey: user.key,
      });
      return true;
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to delete message.');
      return false;
    }
  }, [baseKey, channelKey, user?.key]);

  const threadSummariesByRootKey = useMemo(
    () => buildThreadSummariesByRootKey(allMessages),
    [allMessages],
  );

  return {
    messages,
    threadSummariesByRootKey,
    loading,
    errorMessage,
    isSending,
    hasMoreMessages,
    isLoadingOlder,
    loadOlderMessages,
    sendMessage,
    updateMessage,
    removeMessage,
  };
};

export default useChannelMessages;
